import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import { useState, type ReactNode } from "react";

type Props = {
  id: string;
  title: string;
  description?: string;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
  className?: string;
  testId?: string;
};

export default function DtoolsReviewAccordionSection({
  id,
  title,
  description,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  children,
  className,
  testId,
}: Props) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn("rounded-xl border border-[#1A2744]/12 bg-white shadow-sm", className)}
    >
      <CollapsibleTrigger
        type="button"
        className="flex w-full items-start gap-3 px-4 py-4 text-left sm:px-6"
        data-testid={testId}
        aria-controls={`${id}-content`}
      >
        <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform [[data-state=open]_&]:rotate-90" />
        <div className="min-w-0 flex-1">
          <h2 id={id} className="text-base font-semibold text-[#1A2744] sm:text-lg">
            {title}
          </h2>
          {description ? <p className="mt-1 text-sm text-muted-foreground">{description}</p> : null}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent id={`${id}-content`} className="border-t border-[#1A2744]/8 px-4 pb-5 pt-4 sm:px-6">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}
